import { atom, selectorFamily } from 'recoil';

import { recoilPersist } from 'recoil-persist';

import { isOwnedByBugo } from './giftInputOwner';
import { ThanksSenderSuffix } from '../utils/thanksPrintMessage';

const { persistAtom } = recoilPersist({
  key: 'giftThanksPrint',
  storage: typeof window !== 'undefined' ? localStorage : undefined,
});

/**
 * 감사인쇄 입력값 (localStorage 보존 — 주문하지 않아도 다음 상품·다음 방문에 이어 쓴다).
 *
 * - 감사글은 **카드 디자인(템플릿 이미지)별로** 보존하고, 같은 디자인일 때만 다시 채운다.
 *   디자인마다 문구 칸 크기·지정 문구가 달라서, 다른 디자인에 쓴 글을 자동으로 옮기면
 *   지정 문구 디자인이 자유 입력 글을 덮어쓰거나(예전 실사고) 칸에 안 맞는 글이 인쇄된다.
 *   처음 보는 디자인은 상주 기본 문구로 시작한다. ([다른 카드 감사글 불러오기] 버튼은 폐기 —
 *   입력 중 [완료] 옆에 불쑥 떠서 잘못 누르면 쓰던 글이 확인 없이 덮였다)
 * - 보내는 이(이름·표현)는 디자인과 무관한 상주 정보라 공통.
 * - 보존 범위는 **같은 부고(상주)** 안으로 제한한다 — ownerBugoId 참고.
 */
export interface GiftThanksPrintState {
  /** 발신자 이름 (상주명, 없으면 OOO) */
  senderName: string;
  /** 발신 표현 (배상/드림/올림) */
  senderSuffix: ThanksSenderSuffix;
  /** 카드 디자인(ThanksPrintTemplate._id)별 감사글. 문구 인쇄 스티커(감사글 칸 없음)는 저장하지 않는다 */
  messages?: Record<string, string>;
  /** 최근 고른 디자인 (앞이 최신) — 상품·카드마다 쓸 수 있는 디자인 중 가장 최근 것을 다시 고른다 */
  recentTemplateIds?: string[];
  /** 이 입력값을 작성한 부고(bugo)._id — 다른 상주 주문서에 새어나가지 않게 하는 도장 */
  ownerBugoId?: string;
}

/** 구버전 보존값(감사글 1개 + 마지막 디자인) — 읽을 때만 새 형태로 변환 */
interface LegacyGiftThanksPrintState {
  message?: string;
  brandId?: string;
  templateId?: string;
}

/** 최근 디자인 목록 길이 상한 (localStorage 크기 보호) */
export const THANKS_RECENT_TEMPLATES_MAX = 20;

/**
 * 보존값 정규화 — 구버전 값(message·templateId)은 "그 디자인에 쓴 글"로 옮긴다.
 * 디자인 없이 남은 감사글은 어느 이미지 것인지 몰라 버린다(다음 진입은 상주 기본 문구).
 */
export const normalizeThanksState = (
  raw: (GiftThanksPrintState & LegacyGiftThanksPrintState) | null | undefined,
): GiftThanksPrintState | null => {
  if (!raw) return null;
  const { message, templateId, ...withBrand } = raw;
  const rest: GiftThanksPrintState & { brandId?: string } = { ...withBrand };
  delete rest.brandId;
  const messages = { ...(rest.messages ?? {}) };
  let recent = [...(rest.recentTemplateIds ?? [])];
  if (templateId && message !== undefined && messages[templateId] === undefined) {
    messages[templateId] = message;
  }
  if (templateId && !recent.includes(templateId)) recent = [...recent, templateId];
  return {
    ...rest,
    senderName: rest.senderName ?? '',
    senderSuffix: rest.senderSuffix ?? '배상',
    messages,
    recentTemplateIds: recent.slice(0, THANKS_RECENT_TEMPLATES_MAX),
  };
};

/** 지난 주문의 감사카드 한 장 (주문 문서 thanksPrint / thanksPrints[] 원소) */
export interface PastThanksCard {
  templateId?: unknown;
  message?: string;
  senderName?: string;
}

/**
 * 지난 주문의 카드들 — 여러 장이면 thanksPrints, 아니면 대표 thanksPrint 1장.
 * 감사글이 빈 카드(문구 인쇄 스티커)도 디자인·보내는 이 정보라 남긴다. 첫 장이 스티커여도 나머지 카드가 보이게.
 */
export const pastThanksCards = (order: {
  thanksPrint?: PastThanksCard | null;
  thanksPrints?: PastThanksCard[] | null;
}): { templateId: string; message: string; senderName?: string }[] =>
  (order.thanksPrints?.length ? order.thanksPrints : [order.thanksPrint])
    .filter((c): c is PastThanksCard => !!c?.templateId)
    .map((c) => ({ templateId: String(c.templateId), message: c.message ?? '', senderName: c.senderName }));

/** 지난 주문 카드 중 감사글이 있는 게 하나라도 있는지 (이력의 감사글 표시·채우기 버튼 기준) */
export const hasPastThanksMessage = (order: Parameters<typeof pastThanksCards>[0]): boolean =>
  pastThanksCards(order).some((c) => c.message.trim().length > 0);

/**
 * 지난 주문 카드들을 보존값에 디자인별로 넣는다 — 같은 디자인 상품 카드에서만 채워진다.
 * prev 가 같은 상주 것이면 다른 디자인 글은 그대로 두고 덮어쓰고, 아니면 새로 시작한다.
 * 보내는 이는 첫 카드 것. 스티커(감사글 없음)는 감사글 대신 최근 디자인에만 올린다.
 */
export const withPastThanksCards = (
  prev: GiftThanksPrintState | null,
  bugoId: string,
  cards: ReturnType<typeof pastThanksCards>,
  parseSender: (stored: string | undefined) => { name: string; suffix: ThanksSenderSuffix },
): GiftThanksPrintState | null => {
  const first = cards[0];
  if (!first) return prev;
  const base = prev && isOwnedByBugo(prev.ownerBugoId, bugoId) ? normalizeThanksState(prev) : null;
  const sender = parseSender(first.senderName);
  const ids = cards.map((c) => c.templateId);
  return {
    senderName: sender.name,
    senderSuffix: sender.suffix,
    messages: {
      ...(base?.messages ?? {}),
      ...Object.fromEntries(cards.filter((c) => c.message).map((c) => [c.templateId, c.message])),
    },
    recentTemplateIds: [
      ...ids,
      ...(base?.recentTemplateIds ?? []).filter((id) => !ids.includes(id)),
    ].slice(0, THANKS_RECENT_TEMPLATES_MAX),
    ownerBugoId: bugoId,
  };
};

export const giftThanksPrintAtom = atom<GiftThanksPrintState | null>({
  key: 'giftThanksPrintAtom',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

/**
 * 지금 보고 있는 부고의 감사인쇄 입력값 (명의가 다르면 null, 구버전 값은 새 형태로).
 * 주문서·요약·DTO는 항상 이 selector로 읽어야 한다 — atom을 직접 읽으면
 * 앞서 응대한 다른 상주의 감사글이 그대로 표시/전송된다.
 */
export const giftThanksPrintForBugoSelector = selectorFamily<
  GiftThanksPrintState | null,
  string | null
>({
  key: 'giftThanksPrintForBugoSelector',
  get:
    (bugoId) =>
    ({ get }) => {
      const selection = get(giftThanksPrintAtom);
      return selection && isOwnedByBugo(selection.ownerBugoId, bugoId)
        ? normalizeThanksState(selection)
        : null;
    },
});
